import type React from 'react'
import { connect } from 'react-redux'
import type { NavLinkProps, To } from 'react-router-dom'
import { matchPath, NavLink as RouterLink, useLocation } from 'react-router-dom'
import type { Action } from 'redux'

import { replaceCompanyIdInPath } from '@helpers'

import type { TmsUserPagePermissions } from '@constants'
import { ROUTES } from '@constants'

import { permissionDeniedForUser } from '@permissions'

import { NavLinkTooltipVariant } from '../constants'
import { NoLinkTooltip } from './NoLinkTooltip'

type PermissionFunction = (payload: Company) => boolean

export interface CustomNavLinkProps extends Omit<NavLinkProps, 'to'> {
  activeWhenNoCompany?: boolean
  blockedByOnboarding?: boolean
  children: React.ReactNode
  currentCompany?: Company
  dispatch: React.Dispatch<Action>
  hasCompany: boolean
  inactive: boolean
  linkWhenNoCompany?: boolean
  noSubscription?: boolean
  permission?: TmsUserPagePermissions | PermissionFunction
  to: string
}

function PureCustomNavLink({
  activeWhenNoCompany = false,
  blockedByOnboarding = false,
  currentCompany,
  dispatch, // remove it from rest
  inactive,
  hasCompany,
  linkWhenNoCompany = false,
  noSubscription = false,
  permission,
  to,
  ...rest
}: CustomNavLinkProps) {
  const location = useLocation()
  const match = matchPath(to, location.pathname)

  if (inactive && !linkWhenNoCompany) {
    return <NoLinkTooltip variant={NavLinkTooltipVariant.SELECT}>{rest.children}</NoLinkTooltip>
  }

  const companyId = currentCompany?.id
  const navLinkTo = linkWhenNoCompany ? to : companyId ? replaceCompanyIdInPath(to, companyId) : ROUTES.profile

  let routerLinkTo: To = navLinkTo
  if (match) {
    // when route match build a complete Path object as routerLinkTo to copy searchParams (filters) and hash (optional) to remember the full url
    // otherwise link forgets the UrlSearchParams when repeatedly click on it
    routerLinkTo = {
      pathname: navLinkTo,
      search: location.search,
      hash: location.hash,
    }
  }

  if (permission && currentCompany) {
    // it calls denyTransactionsPermission: hide menu item when it returns true
    let isMenuItemHidden = false
    if (typeof permission === 'function') {
      isMenuItemHidden = permission(currentCompany)
    } else {
      isMenuItemHidden = permissionDeniedForUser(currentCompany, permission as TmsUserPagePermissions, true)
    }
    if (isMenuItemHidden) {
      return null
    }
  }

  if (blockedByOnboarding && currentCompany) {
    return <NoLinkTooltip variant={NavLinkTooltipVariant.ONBOARDING}>{rest.children}</NoLinkTooltip>
  }

  if (noSubscription) {
    return <NoLinkTooltip variant={NavLinkTooltipVariant.SUBSCRIPTION}>{rest.children}</NoLinkTooltip>
  }

  if (companyId || linkWhenNoCompany) {
    return <RouterLink {...rest} to={routerLinkTo} />
  }
  if (hasCompany) {
    if (activeWhenNoCompany) {
      return <RouterLink {...rest} to={ROUTES.root} />
    }
    return <NoLinkTooltip variant={NavLinkTooltipVariant.SELECT}>{rest.children}</NoLinkTooltip>
  }
  if (activeWhenNoCompany) {
    return <RouterLink {...rest} to={ROUTES.noCompany} />
  }
  return <NoLinkTooltip variant={NavLinkTooltipVariant.COMPANY}>{rest.children}</NoLinkTooltip>
}

export const NavLink = connect(
  ({
    auth: {
      companies,
      company: { data: currentCompany, fetched },
    },
  }: Store) => ({
    currentCompany,
    inactive: !fetched,
    hasCompany: Boolean(companies.length),
  })
)(PureCustomNavLink)

NavLink.displayName = 'CustomNavLink'
